import { useEffect, useRef, useState } from 'react';
import type { PortalDog, PortalInfo, PortalStay } from '@bbbk/schema';
import { NOTHING_DUE_CODE, PROOF_PENDING_CODE, formatIban } from '@bbbk/schema';
import { api, ApiError } from '../lib/api';
import { PendingButton } from '../components/common/PendingButton';
import { G } from './glyphs';
import { Tag } from './Tag';
import { LeaveDialog } from './LeaveDialog';
import { BookBrand } from './RequestChrome';
import { MessagePatricia } from './MessagePatricia';
import { depositLeft, dogsOn, nightsLabel, opener, rangeLabel } from './homeRules';
import { dogNames, money } from './requestRules';
import { ProofWell } from './ProofWell';
import { whatsappHref } from './whatsapp';
import { scrollToTopOf } from '../lib/scrollTop';

// THE PAY SHEET — flow 2.3's *How to pay*, in the app's one drawer over Home
// (deposit-proof AC1–AC7, AC18, AC19; Freddi, 2026-10-10: "uploading the
// deposit should open a drawer too with a simplified flow there too").
//
// ONE SCREEN: the amounts, the IBAN, the account name and the reference, each
// copyable, and the upload well beneath them; *Send the screenshot* pinned at
// the foot. Attached, refused, failed and sending are STATES of this sheet,
// not pages (`c8att`, `c9big`, `c9type`, `c9net`, `c9fail`). Sending keeps the
// sheet as it is — the bank details and the file stay on screen, the button
// goes busy (PR #250 review). Sent closes it and Home says so (AC6).
//
// SENT AND LOCKED (AC18, Freddi 2026-10-11): while a screenshot waits on
// Patricia the sheet shows that and offers no well — the card's own third
// state (`c4w`). Accept unlocks the balance (AC19); Not right unlocks a
// re-upload (AC11). NO IBAN YET (AC1c): the sheet says so and offers Patricia,
// never a blank IBAN with a Copy button beside it.

export type SheetChoice = 'full' | 'deposit' | 'balance';
export type SheetFailure = 'net' | 'server' | { message: string } | null;

/** Which line a screenshot is for: the deposit while any is left, the balance while any is due, nothing when paid (AC12, AC19). */
/**
 * The stay a `/pay/<id>` URL opens the sheet for — or none. A stay whose
 * screenshot is with Patricia has no sheet (deposit-proof AC18; Freddi,
 * 2026-10-11: "they should not be able to access the deposit screenshot
 * after uploading"): the URL shows Home, where the notice reads sent.
 */
export function paySheetStay(stays: readonly PortalStay[], id: string): PortalStay | null {
  const stay = stays.find((s) => s.id === id) ?? null;
  // And only while there is something to pay (PR #255 review, #52/#08): a
  // request, a cancelled stay or a settled one has no sheet, whatever the URL.
  return stay && !stay.proofPending && sheetKind(stay) !== null ? stay : null;
}

const PAYABLE = new Set(['unconfirmed', 'confirmed', 'arrived', 'collected']);

export function sheetKind(stay: PortalStay): 'deposit' | 'remainder' | null {
  if (!PAYABLE.has(stay.status) || stay.due <= 0) return null;
  // The deposit while any is left, whatever the status (PR #255 review, #26):
  // a part deposit recorded by hand promotes the booking, and the server's own
  // rule (`proofKindDue`) still files the next screenshot as deposit.
  if (depositLeft(stay) > 0) return 'deposit';
  return 'remainder';
}

/** The page a refused upload belongs on, when the refusal is about the session, not the file (PR #255 review, #25). */
export function uploadLockout(e: unknown): 'signed-out' | 'no-household' | null {
  if (!(e instanceof ApiError)) return null;
  if (e.status === 401) return 'signed-out';
  if (e.status === 403 && e.code === 'no_household') return 'no-household';
  return null;
}

/**
 * SENT IS SENT AT ONCE (PR #255 review, #38). The re-read after an upload is
 * quiet and may fail; the stay is marked waiting from the server's yes, so
 * Home never re-offers a door the server has already closed.
 */
export function markProofSent<M extends { stays: readonly PortalStay[] }>(me: M, stayId: string, kind: 'deposit' | 'remainder'): M {
  if (!me.stays.some((s) => s.id === stayId)) return me;
  return { ...me, stays: me.stays.map((s) => (s.id === stayId ? { ...s, proofPending: kind, proofRefused: null } : s)) };
}

function CopyRow({ label, value, mono = false, icon, isKey = false }: { label: string; value: string; mono?: boolean; icon: React.ReactNode; isKey?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className={'kv-row pay-kv' + (isKey ? ' is-key' : '')}>
      <span className="pay-kv-ic" aria-hidden="true">{icon}</span>
      <dt>{label}</dt>
      <dd className={mono ? 'iban' : ''}>{value}</dd>
      <button type="button" className={'pay-copy' + (copied ? ' copied' : '')} aria-label={`Copy the ${label.toLowerCase()}`} onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => { setCopied(true); window.setTimeout(() => setCopied(false), 1400); }, () => undefined);
      }}>{copied ? G.tick({ size: 12 }) : G.clipboard({ size: 12 })}{copied ? 'Copied' : 'Copy'}</button>
    </div>
  );
}

export interface PaySheetViewProps {
  stay: PortalStay;
  dogs: readonly PortalDog[];
  /** Undefined while it is still being read; null when there is none. */
  info: PortalInfo | null | undefined;
  contactName: string;
  choice: SheetChoice;
  file: File | null;
  /** A file refused before it left the phone (AC3). */
  problem: string | null;
  failure: SheetFailure;
  sending: boolean;
  onChoice: (c: SheetChoice) => void;
  onFile: (file: File | null, problem: string | null) => void;
  onSend: () => void;
  onRetry: () => void;
  onClose: () => void;
}

export function PaySheetView({ stay, dogs, info, contactName, choice, file, problem, failure, sending, onChoice, onFile, onSend, onRetry, onClose }: PaySheetViewProps) {
  const page = useRef<HTMLElement>(null);
  useEffect(() => { scrollToTopOf(page.current); }, []);
  const kind = sheetKind(stay);
  const left = depositLeft(stay);
  const on = dogsOn(stay, dogs);
  const iban = info?.bankIban ? formatIban(info.bankIban) : '';
  // The title is the door that opened it (Freddi, 2026-10-11: "indicative, articulate, succinct").
  const title = kind === 'remainder' ? 'Upload balance proof' : 'Upload deposit proof';
  const message = whatsappHref(opener(kind === 'remainder' ? 'balance' : 'deposit', { contactName, dogs: on.names, stay }));

  // THE MONEY LEADS (Freddi, 2026-10-11: "the customer will be impressed by the drawer"):
  // a brand-tinted hero with the figure to send, the stay one line beneath with its marks.
  const sendNow = kind === 'remainder' ? stay.due : choice === 'full' ? stay.due : left;
  const stayCard = (
    <div className="pay-hero">
      <span className="pay-hero-ic" aria-hidden="true">{G.euro({ size: 20 })}</span>
      <div className="pay-hero-txt">
        <small>{kind === 'remainder' ? 'Balance to send' : 'To send now'}</small>
        <b className="pay-hero-amt">{money(sendNow)}</b>
      </div>
      <div className="pay-hero-stay">
        <span>{G.paw({ size: 13 })}{dogNames(on.names)}</span>
        <span>{G.calendar({ size: 13 })}{rangeLabel(stay.checkIn, stay.checkOut)} · {nightsLabel(stay.checkIn, stay.checkOut)}</span>
        <span className="pay-hero-total">Stay {money(stay.total)}</span>
      </div>
    </div>
  );
  const step = (n: number, t: string) => <div className="pay-step"><span className="pay-step-n">{n}</span><span className="pay-step-t">{t}</span></div>;

  let body: React.ReactNode;
  if (info === undefined) {
    // STILL READING (PR #255 review, #09): the details arrive on their own
    // read, after /me. Until then the rows wait in place — never *no bank
    // details*, which a slow read made every direct /pay link say.
    body = (
      <>
        {stayCard}
        <div className="cp-card pay-bank-wait" aria-busy="true">
          <span className="sk-line" /><span className="sk-line" /><span className="sk-line" />
        </div>
      </>
    );
  } else if (!iban) {
    // NO IBAN YET (AC1c).
    body = (
      <>
        {stayCard}
        <div className="cp-card msg msg-mute pay-noiban">
          <span className="msg-ic">{G.info({ size: 15 })}</span>
          <div className="msg-txt">
            <b>Patricia hasn’t added the bank details yet</b>
            <small>Message her and she’ll send them on — or add them, and this page fills itself in.</small>
          </div>
        </div>
        <div className="cp-actions">
          <PendingButton className="cp-btn primary" icon={G.bubble({ size: 15 })} href={message} external>Message Patricia</PendingButton>
        </div>
      </>
    );
  } else {
    const net = failure === 'net';
    const server = failure === 'server';
    const refusedLast = stay.proofRefused !== null && stay.proofRefused !== undefined;
    const amount = kind === 'remainder' ? stay.due : choice === 'full' ? stay.due : left;
    const well = <ProofWell inputId="pay-file" file={file} problem={problem} sending={sending} title="Attach the screenshot" sub="A screenshot or a PDF of the transfer" refused="That file wasn’t accepted" onFile={onFile} />;
    body = (
      <>
        {stayCard}
        {refusedLast && (
          <div className="cp-card msg msg-stop pay-refused">
            <span className="msg-ic">{G.warn({ size: 15 })}</span>
            <div className="msg-txt"><b>Patricia couldn’t match your last screenshot</b><small>Check the transfer went through, then send a new one.</small></div>
          </div>
        )}
        {step(1, 'How much')}
        {kind === 'remainder' ? (
          <>
            <div className="cp-choice pay-choice is-paid" aria-disabled="true">
              <span className="pay-choice-ic">{G.tick({ size: 14 })}</span>
              <span className="txt"><b>Deposit · {money(stay.deposit)}</b><small>Already in</small></span>
              <Tag tone="ok" glyph={G.tick({ size: 11 })}>Paid</Tag>
            </div>
            <button type="button" className="cp-choice pay-choice on" aria-pressed="true">
              <span className="pay-choice-ic">{G.euro({ size: 14 })}</span>
              <span className="txt"><b>Transfer the balance · {money(stay.due)}</b><small>Nothing more to pay after this.</small></span>
              {G.tick({ size: 14 })}
            </button>
          </>
        ) : (
          <>
            <button type="button" className={'cp-choice pay-choice' + (choice === 'full' ? ' on' : '')} aria-pressed={choice === 'full'} onClick={() => onChoice('full')}>
              <span className="pay-choice-ic">{G.euro({ size: 14 })}</span>
              <span className="txt"><b>Transfer in full · {money(stay.due)}</b><small>Nothing more to pay.</small></span>
              {choice === 'full' && G.tick({ size: 14 })}
            </button>
            <button type="button" className={'cp-choice pay-choice' + (choice === 'deposit' ? ' on' : '')} aria-pressed={choice === 'deposit'} onClick={() => onChoice('deposit')}>
              <span className="pay-choice-ic">{G.euro({ size: 14 })}</span>
              <span className="txt"><b>Transfer the deposit · {money(left)}</b><small>{money(stay.due - left)} due at collection.</small></span>
              {choice === 'deposit' && G.tick({ size: 14 })}
            </button>
          </>
        )}
        {step(2, 'Transfer it')}
        <div className="cp-card pay-bank">
          <dl className="cp-kv">
            <CopyRow label="Account name" value={info?.bankAccountName || 'Beeches Brook Kennels'} icon={G.user({ size: 14 })} />
            <CopyRow label="IBAN" value={iban} mono icon={G.lock({ size: 14 })} />
            <CopyRow label="Reference" value={stay.reference ?? ''} mono icon={G.clipboard({ size: 14 })} isKey />
          </dl>
          <div className="cs-assure">{G.shield({ size: 15 })}<span>Put the reference on your transfer so Patricia can match it to {money(amount)}.</span></div>
        </div>
        {step(3, 'Send the screenshot')}
        {(net || server) && (
          <div className="cp-card msg msg-quiet pay-fail">
            <span className="msg-ic">{net ? G.noSignal({ size: 15 }) : G.warn({ size: 15 })}</span>
            <div className="msg-txt">
              <b>{net ? 'That didn’t reach us' : 'The upload didn’t work'}</b>
              <small>{net ? 'Your connection dropped while the screenshot was sending. Nothing is lost — it’s still on your phone.' : 'Something went wrong on our side while saving your screenshot. Not your fault — and it’s still on your phone.'}</small>
            </div>
          </div>
        )}
        {failure && typeof failure === 'object' && (
          <div className="cp-card msg msg-stop pay-fail"><span className="msg-ic">{G.warn({ size: 15 })}</span><div className="msg-txt"><b>{failure.message}</b></div></div>
        )}
        {well}
        <div className="cp-actions">
          {net || server ? (
            <PendingButton className="cp-btn primary" icon={G.arrowRight({ size: 15 })} onClick={onRetry} busy={sending} disabled={!file}>Try again</PendingButton>
          ) : (
            <PendingButton className="cp-btn primary" icon={G.arrowRight({ size: 15 })} onClick={onSend} busy={sending} disabled={!file || sending}>
              Send the screenshot
            </PendingButton>
          )}
        </div>
      </>
    );
  }

  return (
    <main className="cp-app cp-wizard cp-book cp-pay" ref={page}>
      <div className="cp-scroll cp-step">
        <BookBrand onClose={onClose} busy={sending} />
        <h1 className="cp-h1">{title}</h1>
        <p className="cp-p mute pay-lede">{kind === 'remainder' ? 'Send a bank transfer, then attach a screenshot of it.' : 'Patricia has accepted your dates. Send a bank transfer, then attach a screenshot of it.'}</p>
        {body}
      </div>
    </main>
  );
}

export interface PaySheetProps {
  stay: PortalStay;
  dogs: readonly PortalDog[];
  info: PortalInfo | null | undefined;
  contactName: string;
  /** Sent, or left. */
  onDone: (sent: boolean) => void;
  /** The drawer's pull and Escape ask through here, the way the wizard's do. */
  closeRef?: React.MutableRefObject<(() => void) | null>;
  /** The session went, or the household did, mid-upload (PR #255 review, #25). */
  onLockedOut?: (why: 'signed-out' | 'no-household') => void;
}

export function PaySheet({ stay, dogs, info, contactName, onDone, closeRef, onLockedOut }: PaySheetProps) {
  const [choice, setChoice] = useState<SheetChoice>(sheetKind(stay) === 'remainder' ? 'balance' : 'full');
  const [file, setFile] = useState<File | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [failure, setFailure] = useState<SheetFailure>(null);
  const [sending, setSending] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const send = async () => {
    if (!file || sending) return;
    setSending(true); setFailure(null);
    const form = new FormData();
    form.set('file', file);
    try {
      await api(`/portal/bookings/${encodeURIComponent(stay.id)}/proofs`, { method: 'POST', form, redirectOn401: false });
      onDone(true);
    } catch (e) {
      // THREE TIERS (AC5): it never left the phone, it arrived and we dropped
      // it, or the server said why — a screenshot already waiting, nothing due.
      const locked = uploadLockout(e);
      if (locked && onLockedOut) { onLockedOut(locked); return; }
      if (e instanceof ApiError) {
        if (e.status >= 500) setFailure('server');
        else if (e.code === PROOF_PENDING_CODE || e.code === NOTHING_DUE_CODE) { onDone(true); return; }
        else setFailure({ message: e.message || 'That screenshot wasn’t accepted.' });
      } else setFailure('net');
    } finally {
      setSending(false);
    }
  };
  const leave = () => onDone(false);
  const close = () => { if (sending) return; file ? setLeaving(true) : leave(); };
  if (closeRef) closeRef.current = close;
  return (
    <>
      <PaySheetView stay={stay} dogs={dogs} info={info} contactName={contactName} choice={choice} file={file} problem={problem} failure={failure} sending={sending}
        onChoice={setChoice} onFile={(f, p) => { setFile(f); setProblem(p); setFailure(null); }} onSend={() => void send()} onRetry={() => void send()} onClose={close} />
      {leaving && <LeaveDialog what="screenshot" onStay={() => setLeaving(false)} onLeave={leave} />}
    </>
  );
}
